import { TileType } from '../../types';

// Configuration object for the report grid system
// Defines grid dimensions and size constraints for different tile types
export const REPORT_GRID_CONFIG = {
  GRID: {
    COLUMNS: 12, // Total number of columns in the grid
    ROW_HEIGHT: 10,
  },
  TILE_SIZES: {
    // Default sizes and constraints for different tile types
    [TileType.CARD]: { w: 3, h: 7, minW: 3, minH: 7, maxW: 4, maxH: 13 },
    [TileType.CHART]: { w: 6, h: 23, minW: 3, minH: 21, maxW: 12, maxH: 32 },
    [TileType.TABLE]: { w: 12, h: 18, minW: 8, minH: 10, maxW: 12, maxH: 100 },
    [TileType.NPS_SCORE]: {
      w: 6,
      h: 23,
      minW: 4,
      minH: 21,
      maxW: 6,
      maxH: 26,
    },
  },
} as const;

// Interface defining the structure of a tile
interface Tile {
  id: string | number;
  metadata: {
    componentType: TileType;
  };
}

// Interface defining the structure of a layout item (positioned tile)
interface LayoutItem {
  key: string; // Unique identifier for the tile
  x: number; // X coordinate in grid
  y: number; // Y coordinate in grid
  w: number; // Width in grid units
  h: number; // Height in grid units
  minW: number; // Minimum allowed width
  minH: number; // Minimum allowed height
  maxW: number; // Maximum allowed width
  maxH: number; // Maximum allowed height
}

/**
 * Adds new tiles to the bottom of the existing layout
 * Calculates positions based on the maximum Y coordinate of existing tiles
 * @param newTiles - Array of new tiles to be added
 * @param existingLayout - Current layout of tiles
 * @param isStaticReport - Flag indicating if this is a static report (optional)
 * @returns Array of layout items for the new tiles
 */
const appendTilesAtBottom = (
  newTiles,
  existingLayout: LayoutItem[],
  isStaticReport: boolean = false
): LayoutItem[] => {
  const maxY = getMaxY(existingLayout);
  let currentX = 0;
  let currentY = maxY;

  return newTiles.map((tile) => {
    const componentType = tile.metadata.componentType;
    // Get size configuration for the tile type, fallback to CARD if type not found
    const sizeConfig =
      REPORT_GRID_CONFIG.TILE_SIZES[componentType] ||
      REPORT_GRID_CONFIG.TILE_SIZES[TileType.CARD];

    // For static reports, allow narrower cards (minW=2) so 5-card rows fit
    const constraints = {
      ...sizeConfig,
      ...(isStaticReport && componentType === TileType.CARD
        ? { maxW: 6, minW: 2 }
        : {}),
    };

    // Move to next row if current row is full
    if (currentX + constraints.w > REPORT_GRID_CONFIG.GRID.COLUMNS) {
      currentX = 0;
      currentY++;
    }

    const layoutItem: LayoutItem = {
      key: tile.id.toString(),
      x: currentX,
      y: currentY,
      w: constraints.w,
      h: constraints.h,
      minW: constraints.minW,
      minH: constraints.minH,
      maxW: constraints.maxW,
      maxH: constraints.maxH,
    };

    currentX += constraints.w;
    return layoutItem;
  });
};

/**
 * Main function to synchronize layout with tiles
 * Handles adding new tiles, preserving existing positions, and ensuring layout integrity
 * @param tiles - Array of all tiles (new and existing)
 * @param existingLayout - Current layout configuration
 * @param isStaticReport - Flag indicating if this is a static report (optional)
 * @returns Updated layout configuration
 */
export const syncLayoutWithTiles = (
  tiles,
  existingLayout: LayoutItem[],
  isStaticReport: boolean = false
): LayoutItem[] => {
  // Generate new layout if none exists
  if (!existingLayout?.length) {
    return generateInitialLayout(tiles, isStaticReport);
  }

  // Create quick lookup map for existing layouts
  const existingLayoutMap = new Map<string, LayoutItem>();
  existingLayout.forEach((item) => {
    existingLayoutMap.set(item.key, item);
  });

  // Find tiles that aren't in the existing layout
  const existingTileIds = new Set(existingLayout.map((item) => item.key));
  const newTiles = tiles.filter(
    (tile) => !existingTileIds.has(tile.id.toString())
  );

  // Create new layout preserving existing positions where possible
  let newLayout = tiles
    .filter((tile) => existingLayoutMap.has(tile.id.toString()))
    .map((tile) => {
      const key = tile.id.toString();
      const existingItem = existingLayoutMap.get(key)!;
      const componentType = tile.metadata.componentType;
      const sizeConfig =
        REPORT_GRID_CONFIG.TILE_SIZES[componentType] ||
        REPORT_GRID_CONFIG.TILE_SIZES[TileType.CARD];

      // Apply static report override for CARD tiles if needed
      const sizeWithOverrides = {
        ...sizeConfig,
        ...(isStaticReport && componentType === TileType.CARD
          ? { maxW: 6, minW: 2 }
          : {}),
      };

      // Preserve original constraint values from existingItem if they exist
      // Only use defaults for w and h adjustments
      const layoutItem: LayoutItem = {
        key,
        x: existingItem.x,
        y: existingItem.y,
        w: Math.min(
          Math.max(existingItem.w, sizeWithOverrides.minW),
          sizeWithOverrides.maxW
        ),
        h: Math.min(
          Math.max(existingItem.h, sizeWithOverrides.minH),
          sizeWithOverrides.maxH
        ),
        // Preserve original constraints instead of using defaults
        minW:
          existingItem.minW !== undefined
            ? existingItem.minW
            : sizeWithOverrides.minW,
        minH:
          existingItem.minH !== undefined
            ? existingItem.minH
            : sizeWithOverrides.minH,
        maxW:
          existingItem.maxW !== undefined
            ? existingItem.maxW
            : sizeWithOverrides.maxW,
        maxH:
          existingItem.maxH !== undefined
            ? existingItem.maxH
            : sizeWithOverrides.maxH,
      };

      return layoutItem;
    });

  // Add new tiles at the bottom if any exist
  if (newTiles.length > 0) {
    const newTileLayouts = appendTilesAtBottom(
      newTiles,
      newLayout,
      isStaticReport
    );
    newLayout = [...newLayout, ...newTileLayouts];
  }

  // Verify and adjust layout to ensure all tiles fit within grid
  return newLayout.map((item) => {
    const adjustedItem: LayoutItem = { ...item };

    // Move item to next row if it extends beyond grid width
    if (adjustedItem.x + adjustedItem.w > REPORT_GRID_CONFIG.GRID.COLUMNS) {
      adjustedItem.x = 0;
      adjustedItem.y = getMaxY(newLayout);
    }

    return adjustedItem;
  });
};

/**
 * Generates initial layout for tiles when no existing layout exists
 * Places tiles sequentially, moving to new rows when needed
 * @param tiles - Array of tiles to generate layout for
 * @param isStaticReport - Flag indicating if this is a static report (optional)
 * @returns Initial layout configuration
 */
const generateInitialLayout = (
  tiles: Tile[],
  isStaticReport: boolean = false
): LayoutItem[] => {
  return tiles.reduce<LayoutItem[]>((layout, tile) => {
    const componentType = tile.metadata.componentType;
    const sizeConfig =
      REPORT_GRID_CONFIG.TILE_SIZES[componentType] ||
      REPORT_GRID_CONFIG.TILE_SIZES[TileType.CARD];

    // Apply static report override for CARD tiles
    const constraints = {
      ...sizeConfig,
      ...(isStaticReport && componentType === TileType.CARD
        ? { maxW: 6, minW: 2 }
        : {}),
    };

    const lastItem = layout[layout.length - 1];
    let x = 0;
    let y = lastItem ? lastItem.y : 0;

    // Calculate position based on last item
    if (lastItem) {
      x = lastItem.x + lastItem.w;
      if (x + constraints.w > REPORT_GRID_CONFIG.GRID.COLUMNS) {
        x = 0;
        y = lastItem.y + lastItem.h;
      }
    }

    layout.push({
      key: tile.id.toString(),
      x,
      y,
      w: constraints.w,
      h: constraints.h,
      minW: constraints.minW,
      minH: constraints.minH,
      maxW: constraints.maxW,
      maxH: constraints.maxH,
    });

    return layout;
  }, []);
};

/**
 * Helper function to find the maximum Y coordinate in the layout
 * Used to determine where to place new tiles
 * @param layout - Current layout configuration
 * @returns Maximum Y coordinate plus height of the item at that coordinate
 */
const getMaxY = (layout: LayoutItem[]): number => {
  return layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
};
